import React from 'react';
import type AkCustomThemeButton from '@atlaskit/button/custom-theme-button';
import type AkButton from '@atlaskit/button';
import { type AnalyticsAttributes } from '../../types';
import { useAnalytics } from '../';
import { InstrumentedButtonAnalytics } from './InstrumentedButtonAnalytics';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';

export type AddedProps = {
	actionSubjectId: string;
	analyticsAttributes?: AnalyticsAttributes | (() => AnalyticsAttributes);
};

export const asInstrumentedButton = <
	InnerProps extends
		| React.ComponentPropsWithoutRef<typeof AkCustomThemeButton>
		| React.ComponentPropsWithoutRef<typeof AkButton>,
>(
	Button: React.FC<Omit<InnerProps, keyof AddedProps>>,
) =>
	React.forwardRef<
		| {
				onClick: (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
		  }
		| undefined,
		AddedProps & InnerProps
	>(({ actionSubjectId, analyticsAttributes, ...props }, ref) => {
		const analytics = useAnalytics(InstrumentedButtonAnalytics);
		const onClick: InnerProps['onClick'] = (reactEvent, analyticsEvent) => {
			analytics.buttonClicked(
				actionSubjectId,
				typeof analyticsAttributes === 'function' ? analyticsAttributes() : analyticsAttributes,
			);
			return props.onClick?.(reactEvent, analyticsEvent);
		};

		React.useImperativeHandle(ref, () => ({
			onClick: async (event, analyticsEvent) => {
				await onClick(event, analyticsEvent);
			},
		}));

		if (ref) {
			return null;
		}

		return <Button {...props} onClick={onClick} ref={ref} />;
	});
